import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Save } from 'lucide-react';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { SectionCard } from '@/components/common/SectionCard';
import Button from '@/components/common/Button';
import FormField from '@/components/common/FormField';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification, RolesTypes, ResourceLabelMap } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { RoutesLink } from '@/utility/RouteLinks';
import { useActiveRole } from '@/hooks/useActiveRole';

interface RoleForm {
    name: string;
    [key: string]: any;
}

interface PermissionPayload {
    resource: string;
    actions: string[]; // grouped: ['view', 'create', ...]
}

export const AddEditRole: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { prefix } = useActiveRole();
    const isEdit = Boolean(id);
    const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<RoleForm>({ defaultValues: { name: '' } });

    const watchAll = watch();

    // ── Fetch existing role for edit mode ────────────────────────────────
    const fetchRoleData = async () => {
        setLoadingRoles(true);
        try {
            const res = await callApi<any>(
                `${API_URL}${API_ENDPOINT.roles}/${id}`,
                { method: 'GET' }
            );
            const roleData = res.data || res;
            const defaults: RoleForm = { name: roleData.name };

            // Restore saved permission checkboxes — handle stringified JSON array
            let hasWildcard = false;
            (roleData.permissions || []).forEach((perm: any) => {
                let actionList: string[] = [];
                try {
                    if (perm.resource === '*') {
                        hasWildcard = true;
                    }
                    actionList = typeof perm.actions === 'string' ? JSON.parse(perm.actions) : perm.actions;
                } catch {
                    actionList = [perm.actions];
                }

                if (Array.isArray(actionList) && actionList.includes('*')) {
                    hasWildcard = true;
                }

                if (Array.isArray(actionList)) {
                    actionList.forEach((action) => {
                        defaults[`${perm.resource}-${action}`] = true;
                    });
                }
            });

            if (hasWildcard) {
                setIsAdmin(true);
                RolesTypes.forEach(({ resource, actions }) => {
                    actions.forEach((action) => {
                        defaults[`${resource}-${action}`] = true;
                    });
                });
            }

            reset(defaults);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingRoles(false);
        }
    };

    useEffect(() => {
        if (id) fetchRoleData();
    }, [id]);

    // ── Submit ────────────────────────────────────────────────────────────
    const onSubmit = async (data: RoleForm) => {
        try {
            // Group selected actions per resource → [{ resource, actions: ['view', 'create'] }]
            let permissions: PermissionPayload[] = [];
            if (isAdmin) {
                permissions = [];
            } else {
                permissions = RolesTypes
                    .map(({ resource, actions }) => {
                        const selectedActions = actions.filter(
                            (action) => watchAll[`${resource}-${action}`]
                        );
                        return selectedActions.length
                            ? { resource, actions: selectedActions }
                            : null;
                    })
                    .filter(Boolean) as PermissionPayload[];
            }

            const payload = { name: data.name, permissions };
            const url = id
                ? `${API_URL}${API_ENDPOINT.roles}/${id}`
                : `${API_URL}${API_ENDPOINT.roles}`;

            await callApi(url, { method: id ? 'PATCH' : 'POST', data: payload });
            showToastNotification('success', `Role ${id ? 'updated' : 'created'} successfully.`);
            navigate(prefix + RoutesLink.Roles);
        } catch (error) {
            ThrowError(error);
        }
    };

    // ── Select / Deselect all actions for a resource ─────────────────────
    const handleSelectResource = (resource: string, actions: string[], checked: boolean) => {
        actions.forEach((action) => setValue(`${resource}-${action}`, checked));
    };

    // ── Select / Deselect all permissions ────────────────────────────────
    const handleSelectAll = (checked: boolean) => {
        RolesTypes.forEach(({ resource, actions }) => {
            actions.forEach((action) => setValue(`${resource}-${action}`, checked));
        });
    };

    const allSelected = RolesTypes.every(({ resource, actions }) =>
        actions.every((a) => watchAll[`${resource}-${a}`])
    );

    if (loadingRoles) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-500">

                <MainHeading
                    title={isEdit ? 'Edit Role' : 'Add Role'}
                    description={isEdit ? 'Update role name and permissions.' : 'Create a new role and assign permissions.'}
                    showBack
                    onBack={() => navigate(-1)}
                />

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Role Name */}
                    <SectionCard title="Role Information">
                        <div className="max-w-sm">
                            <FormField
                                label="Role Name"
                                type="text"
                                placeholder="e.g. Store Staff"
                                {...register('name', { ...errorTypes.required('Role Name') })}
                                error={errors.name?.message}
                                // disabled={isAdmin}
                                requiredIcon
                            />
                        </div>
                    </SectionCard>

                    {/* Permission Matrix */}
                    <SectionCard title="Permissions">
                        {/* Select All */}
                        <div className="flex items-center gap-3 pb-4 mb-4 border-b border-border/50">
                            <input
                                type="checkbox"
                                id="select-all"
                                checked={allSelected || isAdmin}
                                disabled={isAdmin}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className="w-4 h-4 accent-primary cursor-pointer rounded"
                            />
                            <label htmlFor="select-all" className="text-sm font-semibold text-foreground cursor-pointer select-none">
                                Select All Permissions
                            </label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {RolesTypes.map(({ resource, actions }) => {
                                const resourceAllSelected = actions.every(
                                    (a) => watchAll[`${resource}-${a}`]
                                );
                                return (
                                    <div
                                        key={resource}
                                        className="rounded-xl border border-border/50 overflow-hidden bg-secondary/5 hover:border-primary/20 transition-colors"
                                    >
                                        {/* Resource header */}
                                        <div className="flex items-center gap-3 px-4 py-3 bg-primary/5 border-b border-border/40">
                                            <input
                                                type="checkbox"
                                                id={`res-${resource}`}
                                                checked={resourceAllSelected || isAdmin}
                                                disabled={isAdmin}
                                                onChange={(e) =>
                                                    handleSelectResource(resource, actions, e.target.checked)
                                                }
                                                className="w-4 h-4 accent-primary cursor-pointer rounded"
                                            />
                                            <label
                                                htmlFor={`res-${resource}`}
                                                className="text-sm font-semibold text-foreground capitalize cursor-pointer select-none"
                                            >
                                                {ResourceLabelMap[resource] || resource}
                                            </label>
                                        </div>

                                        {/* Action checkboxes */}
                                        <div className="px-4 py-3 flex flex-wrap gap-3">
                                            {actions.map((action) => {
                                                const key = `${resource}-${action}`;
                                                return (
                                                    <label
                                                        key={action}
                                                        className="flex items-center gap-1.5 cursor-pointer select-none"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            {...register(key)}
                                                            className="w-3.5 h-3.5 accent-primary rounded"
                                                            disabled={isAdmin}
                                                        />
                                                        <span className="text-xs text-muted-foreground capitalize">{action}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </SectionCard>

                    {/* Submit */}
                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            isLoading={isSubmitting}
                            loadingText="Saving..."
                            leftIcon={<Save className="w-4 h-4" />}
                        >
                            {isEdit ? 'Update Role' : 'Create Role'}
                        </Button>
                    </div>
                </form>
            </div>
        </Layout>
    );
};
